/**
 * Admin > AI Models - which Claude model does which job, and is any of it stale.
 *
 * Read-only by design. Changing the model that reads plan documents changes
 * what lands in the plan-design registry, so it belongs in a diff with a re-run
 * behind it, not in a dropdown. What was actually missing was visibility: the
 * ids used to live as 18 literals across six BFF modules, which is how two of
 * them reached Opus 5 while the plan-design extractor sat on 4.8 unnoticed.
 *
 * The page renders the same registry the server code reads, so it cannot
 * describe a model the app is not really using.
 */

import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Body1,
  Caption1,
  MessageBar,
  MessageBarBody,
  Spinner,
  Title2,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { BrainCircuitRegular } from '@fluentui/react-icons';
import { bffFetch } from '@shared/api';
import { veracityColors } from '@shared/ui';

interface RoleRow {
  role: string;
  model: string;
  purpose: string;
  rationale: string;
  envOverride: string | null;
  overridden: boolean;
  pinnedDefault: string;
  /** null = the upstream check could not run. */
  offeredUpstream: boolean | null;
}

interface AiModelsResponse {
  roles: RoleRow[];
  upstream: { checked: boolean; error: string; available: string[] };
}

/** camelCase role key to something readable, without a second list to maintain. */
function roleLabel(role: string): string {
  return role.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

const useStyles = makeStyles({
  page: { padding: tokens.spacingVerticalXXL, maxWidth: '1000px', margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  intro: { marginTop: tokens.spacingVerticalS, color: tokens.colorNeutralForeground2 },
  list: {
    marginTop: tokens.spacingVerticalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  card: {
    border: `1px solid ${veracityColors.firstLight}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalL,
    backgroundColor: '#FFFFFF',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  roleName: { fontWeight: tokens.fontWeightSemibold },
  model: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase300,
  },
  badges: { display: 'flex', gap: tokens.spacingHorizontalXS, alignItems: 'center' },
  purpose: { marginTop: tokens.spacingVerticalXS },
  rationale: {
    marginTop: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground3,
  },
  footNote: { marginTop: tokens.spacingVerticalXXL, color: tokens.colorNeutralForeground3 },
  availList: {
    marginTop: tokens.spacingVerticalS,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: '1.7',
  },
});

export function AiModelsPage() {
  const styles = useStyles();
  const { data, isLoading, error } = useQuery<AiModelsResponse>({
    queryKey: ['admin', 'ai-models'],
    queryFn: async () => {
      const res = await bffFetch('/api/admin/ai-models');
      if (!res.ok) throw new Error('Could not load the model registry.');
      return res.json();
    },
  });

  if (isLoading) return <div className={styles.page}><Spinner label="Loading model registry" /></div>;
  if (error || !data) {
    return (
      <div className={styles.page}>
        <MessageBar intent="error">
          <MessageBarBody>Could not load the model registry.</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  const stale = data.roles.filter((r) => r.offeredUpstream === false);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <BrainCircuitRegular fontSize={28} color={veracityColors.veracityOrange} />
        <Title2>AI Models</Title2>
      </div>
      <Body1 className={styles.intro}>
        Which Claude model runs which job. Read-only: changing a model changes what
        extraction writes into the plan-design registry, so it is a code change with a
        re-run behind it rather than a setting.
      </Body1>

      {stale.length > 0 && (
        <MessageBar intent="warning" style={{ marginTop: tokens.spacingVerticalL }}>
          <MessageBarBody>
            {stale.length === 1 ? 'One role names a model' : `${stale.length} roles name models`}{' '}
            the API no longer offers: {stale.map((r) => r.model).join(', ')}. Calls using{' '}
            {stale.length === 1 ? 'it' : 'them'} will fail.
          </MessageBarBody>
        </MessageBar>
      )}

      {!data.upstream.checked && (
        <MessageBar intent="info" style={{ marginTop: tokens.spacingVerticalL }}>
          <MessageBarBody>
            Could not check these against the Models API, so staleness is unknown here:{' '}
            {data.upstream.error}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.list}>
        {data.roles.map((r) => (
          <div key={r.role} className={styles.card}>
            <div className={styles.cardTop}>
              <span className={styles.roleName}>{roleLabel(r.role)}</span>
              <div className={styles.badges}>
                <span className={styles.model}>{r.model}</span>
                {r.offeredUpstream === true && (
                  <Badge appearance="tint" color="success">Current</Badge>
                )}
                {r.offeredUpstream === false && (
                  <Badge appearance="tint" color="danger">Not offered</Badge>
                )}
                {r.offeredUpstream === null && (
                  <Badge appearance="tint" color="informative">Unchecked</Badge>
                )}
                {r.overridden && (
                  <Tooltip
                    relationship="description"
                    content={`${r.envOverride} overrides the pinned default (${r.pinnedDefault}) on this server.`}
                  >
                    <Badge appearance="tint" color="warning">Env override</Badge>
                  </Tooltip>
                )}
              </div>
            </div>
            <Body1 className={styles.purpose}>{r.purpose}</Body1>
            <Caption1 className={styles.rationale}>{r.rationale}</Caption1>
          </div>
        ))}
      </div>

      {data.upstream.available.length > 0 && (
        <div className={styles.footNote}>
          <Caption1>Models this account can call:</Caption1>
          <div className={styles.availList}>{data.upstream.available.join(' · ')}</div>
        </div>
      )}
    </div>
  );
}

export default AiModelsPage;
